import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, Download, Mail, Eye, ChevronUp, ChevronDown, FilePlus } from "lucide-react";
import { exportToCsv } from "@/lib/export-csv";

export type ReceivableRow = {
  recipientId: number;
  recipientType: 'owner' | 'org';
  recipientName: string;
  recipientEmail: string | null;
  outstandingTotal: number;
  pendingCharges: number;
  totalExposure: number;
  pendingFrom: string | null;
  pendingTo: string | null;
  unpaidInvoiceCount: number;
  oldestUnpaidIssuedAt: string | null;
  daysOverdue: number;
  lastReminderSentAt: string | null;
  invoiceStatus: 'invoiced' | 'pending_invoice' | 'both';
};

type SortField = 'totalExposure' | 'daysOverdue' | 'unpaidInvoiceCount' | 'oldestUnpaidIssuedAt';

type Props = {
  data: ReceivableRow[] | undefined;
  isLoading: boolean;
  onViewRecipient: (row: ReceivableRow) => void;
  onSendReminder: (row: ReceivableRow) => void;
  onGenerateInvoice: (ownerIds: number[], periodFrom?: string | null, periodTo?: string | null) => void;
  sortBy: SortField;
  sortDir: 'asc' | 'desc';
  onSortChange: (field: SortField, dir: 'asc' | 'desc') => void;
  search: string;
  onSearchChange: (s: string) => void;
  minDaysOverdue: number;
  onMinDaysOverdueChange: (d: number) => void;
};

function fmt(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysOverdueBadge(days: number) {
  if (days === 0) return <Badge variant="outline" className="text-xs text-muted-foreground">Current</Badge>;
  if (days <= 30) return <Badge variant="outline" className="text-xs text-green-600 border-green-300">{days}d</Badge>;
  if (days <= 60) return <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">{days}d</Badge>;
  if (days <= 90) return <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">{days}d</Badge>;
  return <Badge variant="destructive" className="text-xs">{days}d</Badge>;
}

function statusBadge(status: ReceivableRow['invoiceStatus']) {
  if (status === 'invoiced') return <Badge variant="default" className="text-xs bg-blue-100 text-blue-800 border-blue-200">Invoiced</Badge>;
  if (status === 'pending_invoice') return <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50">Awaiting Invoice</Badge>;
  return <Badge variant="outline" className="text-xs text-purple-700 border-purple-300 bg-purple-50">Invoice + Pending</Badge>;
}

export default function ReceivablesTable({
  data, isLoading, onViewRecipient, onSendReminder, onGenerateInvoice,
  sortBy, sortDir, onSortChange, search, onSearchChange,
  minDaysOverdue, onMinDaysOverdueChange,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const rows = data ?? [];
  const allKeys = rows.map(r => `${r.recipientType}:${r.recipientId}`);
  const allSelected = allKeys.length > 0 && allKeys.every(k => selected.has(k));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allKeys));
  };

  const toggle = (key: string) => {
    const s = new Set(selected);
    if (s.has(key)) s.delete(key); else s.add(key);
    setSelected(s);
  };

  const selectedRows = rows.filter(r => selected.has(`${r.recipientType}:${r.recipientId}`));
  const selectedWithPending = selectedRows.filter(r => r.invoiceStatus !== 'invoiced' && r.recipientType === 'owner');

  const handleSort = (field: SortField) => {
    if (sortBy === field) onSortChange(field, sortDir === 'asc' ? 'desc' : 'asc');
    else onSortChange(field, 'desc');
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'desc' ? <ChevronDown className="h-3 w-3 ml-1" /> : <ChevronUp className="h-3 w-3 ml-1" />;
  };

  const handleExport = () => {
    const headers = ['Recipient', 'Type', 'Email', 'Status', 'Total Owed', '# Invoices', 'Oldest Issued', 'Days Overdue', 'Last Reminder'];
    const csvRows = rows.map(r => [
      r.recipientName, r.recipientType, r.recipientEmail ?? '',
      r.invoiceStatus,
      r.totalExposure.toFixed(2),
      String(r.unpaidInvoiceCount),
      fmtDate(r.oldestUnpaidIssuedAt), String(r.daysOverdue), fmtDate(r.lastReminderSentAt),
    ]);
    exportToCsv('receivables.csv', headers, csvRows);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">Accounts Receivable</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              className="h-8 text-xs w-48"
              placeholder="Search recipient…"
              value={search}
              onChange={e => onSearchChange(e.target.value)}
            />
            <Select
              value={String(minDaysOverdue)}
              onValueChange={v => onMinDaysOverdueChange(Number(v))}
            >
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All overdue</SelectItem>
                <SelectItem value="30">30+ days overdue</SelectItem>
                <SelectItem value="60">60+ days overdue</SelectItem>
                <SelectItem value="90">90+ days overdue</SelectItem>
              </SelectContent>
            </Select>
            {selectedWithPending.length > 0 && (
              <Button
                size="sm"
                variant="default"
                className="h-8 text-xs"
                onClick={() => {
                  const froms = selectedWithPending.map(r => r.pendingFrom).filter(Boolean) as string[];
                  const tos = selectedWithPending.map(r => r.pendingTo).filter(Boolean) as string[];
                  const from = froms.length ? froms.reduce((a, b) => a < b ? a : b) : null;
                  const to = tos.length ? tos.reduce((a, b) => a > b ? a : b) : null;
                  onGenerateInvoice(selectedWithPending.map(r => r.recipientId), from, to);
                  setSelected(new Set());
                }}
              >
                <FilePlus className="h-3 w-3 mr-1" /> Generate {selectedWithPending.length} Invoice{selectedWithPending.length > 1 ? 's' : ''}
              </Button>
            )}
            {selectedRows.length > 0 && selectedWithPending.length === 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => { selectedRows.forEach(r => onSendReminder(r)); setSelected(new Set()); }}
              >
                <Mail className="h-3 w-3 mr-1" /> Send {selectedRows.length} reminder{selectedRows.length > 1 ? 's' : ''}
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleExport}>
              <Download className="h-3 w-3 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground text-sm">
            No outstanding balances — all invoices are current.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                </TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <button className="flex items-center text-xs font-medium" onClick={() => handleSort('unpaidInvoiceCount')}>
                    # Invoices <SortIcon field="unpaidInvoiceCount" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center text-xs font-medium" onClick={() => handleSort('totalExposure')}>
                    Total Owed <SortIcon field="totalExposure" />
                  </button>
                </TableHead>
                <TableHead>Charge Period</TableHead>
                <TableHead>
                  <button className="flex items-center text-xs font-medium" onClick={() => handleSort('oldestUnpaidIssuedAt')}>
                    Oldest Invoice <SortIcon field="oldestUnpaidIssuedAt" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center text-xs font-medium" onClick={() => handleSort('daysOverdue')}>
                    Days Overdue <SortIcon field="daysOverdue" />
                  </button>
                </TableHead>
                <TableHead>Last Reminder</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => {
                const key = `${row.recipientType}:${row.recipientId}`;
                const canGenerate = row.invoiceStatus !== 'invoiced' && row.recipientType === 'owner';
                return (
                  <TableRow
                    key={key}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onViewRecipient(row)}
                  >
                    <TableCell onClick={e => { e.stopPropagation(); toggle(key); }}>
                      <Checkbox checked={selected.has(key)} onCheckedChange={() => toggle(key)} />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{row.recipientName}</p>
                        <p className="text-xs text-muted-foreground">
                          {row.recipientType === 'owner' ? 'Owner' : 'Organization'}
                          {row.recipientEmail ? ` · ${row.recipientEmail}` : ''}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{statusBadge(row.invoiceStatus)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">{row.unpaidInvoiceCount}</Badge>
                    </TableCell>
                    <TableCell className={`font-semibold text-sm ${row.totalExposure > 5000 ? 'text-red-600' : ''}`}>
                      {fmt(row.totalExposure)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {row.pendingFrom && row.pendingTo
                        ? row.pendingFrom === row.pendingTo
                          ? fmtDate(row.pendingFrom)
                          : <>{fmtDate(row.pendingFrom)}<br />→ {fmtDate(row.pendingTo)}</>
                        : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmtDate(row.oldestUnpaidIssuedAt)}</TableCell>
                    <TableCell>{daysOverdueBadge(row.daysOverdue)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmtDate(row.lastReminderSentAt)}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onViewRecipient(row)} title="View invoices">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {canGenerate ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-amber-600 hover:text-amber-700"
                            onClick={() => onGenerateInvoice([row.recipientId], row.pendingFrom, row.pendingTo)}
                            title="Create invoice for pending charges"
                          >
                            <FilePlus className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onSendReminder(row)} title="Send reminder">
                            <Mail className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
